import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Grid, 
  List, 
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
  ChevronDown,
  X,
  Package,
  Star,
  Check,
  Building,
  Eye,
  Heart,
  Share2,
  MessageCircle,
  Truck,
  Shield,
  TrendingUp,
  MapPin,
  FileText,
  Tag,
  Info,
  Menu,
  User,
  ExternalLink,
  Palette,
  Box,
  CheckCircle,
  Percent,
  ShieldCheck, // For warranty
  Gauge, // For MOQ indicator
  Clock, // For warranty duration
  Award // For warranty badge
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import Card from '../../../components/ui/Card';
import { db, collection, getDocs, query, orderBy, limit, where } from '../../../firebase/config';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { createChatWithSeller } from '../../../services/chatService';

const ProductsPage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedProductForImages, setSelectedProductForImages] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    inStockOnly: false
  });
  
  // New state for bottom success message
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const productsPerPage = 30;
  const [startingChat, setStartingChat] = useState(false);
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const location = useLocation();
  
  const calculateTotalPrice = () => {
    if (!product) return 0;
    return product.price * quantity;
  };
  
  const [quantity, setQuantity] = useState(1);

  const handleStartChat = async () => {
    try {
      if (!user || !product || !product.sellerId) {
        toast.error('Please login to start a chat');
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      
      setStartingChat(true);
      
      const buyerName = user.displayName || user.name || user.email?.split('@')[0] || 'Buyer';
      const buyerEmail = user.email;
      
      const chatData = {
        buyerId: user.uid,
        buyerName: buyerName,
        buyerEmail: buyerEmail,
        buyerPhoto: user.photoURL || '',
        
        sellerId: product.sellerId,
        sellerName: product.sellerName || product.companyName || 'Seller',
        sellerEmail: product.sellerEmail || product.sellerContactEmail || '',
        sellerVerified: product.sellerVerified || false,
        
        productId: product.id,
        productName: product.name,
        productImage: product.images?.[0]?.data || '',
        
        initialMessage: `Hi, I'm interested in your product "${product.name}". Can you provide more details about:`,
        quantity: quantity || product.moq || 1,
        unit: product.unit || 'PCS',
        totalPrice: calculateTotalPrice() || product.price || 0,
        moq: product.moq || 1,
        unitPrice: product.price || 0,
        colors: product.colors || '',
        warranty: product.warranty || 'No Warranty' // NEW: Include warranty in chat
      };
      
      console.log('🟡 Creating chat with data:', chatData);
      
      const chatId = await createChatWithSeller(chatData);
      console.log('✅ Chat created with ID:', chatId);
      
      toast.success('Chat started successfully! Redirecting...');
      
      navigate(`/chat/${chatId}`);
      
    } catch (error) {
      console.error('❌ Error creating chat:', error);
      
      if (error.message.includes('Missing required')) {
        toast.error('Unable to start chat. Product information is incomplete.');
      } else if (error.message.includes('permission-denied')) {
        toast.error('Permission denied. Please try logging in again.');
      } else {
        toast.error(`Failed to start chat: ${error.message}`);
      }
    } finally {
      setStartingChat(false);
    }
  };

  // Show success message at bottom
  const showBottomSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    
    // Auto hide after 3 seconds
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  // Format warranty for display with professional styling
  const formatWarranty = (warranty) => {
    if (!warranty || warranty === 'No Warranty') {
      return {
        text: 'No Warranty',
        icon: Shield,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200'
      };
    }
    
    if (warranty.includes('Lifetime')) {
      return {
        text: 'Lifetime Warranty',
        icon: Award,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        borderColor: 'border-purple-200'
      };
    }
    
    if (warranty.includes('Manufacturer')) {
      return {
        text: warranty,
        icon: ShieldCheck,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-200'
      };
    }
    
    if (warranty.includes('Seller')) {
      return {
        text: warranty,
        icon: Shield,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200'
      };
    }
    
    // Default for warranty periods (1 Year, 2 Years, etc.)
    return {
      text: warranty,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200'
    };
  };

  // Format MOQ display with unit
  const formatMOQ = (moq, unit) => {
    if (!moq) return 'Not specified';
    return `${moq} ${unit || 'PCS'}`;
  };

  // Fetch ONLY APPROVED products from Firebase
  const fetchApprovedProducts = async () => {
    try {
      setLoading(true);
      console.log('Fetching approved products from Firebase...');
      
      const productsRef = collection(db, 'products');
      
      const q = query(
        productsRef, 
        where('status', '==', 'APPROVED'),
        orderBy('createdAtTimestamp', 'desc'),
        limit(1000)
      );

      const querySnapshot = await getDocs(q);
      const productsList = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        if (!data.name || data.price === undefined) {
          console.warn(`Skipping product ${doc.id}: Missing required fields`);
          return;
        }
        
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
        const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date();
        const createdAtTimestamp = data.createdAtTimestamp || 
                                (data.createdAt?.toDate ? data.createdAt.toDate().getTime() : Date.now());
        
        productsList.push({
          id: doc.id,
          ...data,
          createdAt,
          updatedAt,
          createdAtTimestamp,
          name: data.name || 'Unnamed Product',
          category: data.category || 'Uncategorized',
          price: data.price || 0,
          gst: data.gst || '18%',
          warranty: data.warranty || 'No Warranty', // NEW: Fetch warranty field
          moq: data.moq || 1, // NEW: Fetch MOQ field
          rating: data.rating || 0,
          reviewCount: data.reviewCount || 0,
          stock: data.stock || 0,
          sellerName: data.sellerName || 'Unknown Seller',
          sellerVerified: data.sellerVerified || false,
          businessType: data.businessType || 'Manufacturer',
          city: data.city || 'Not Specified',
          material: data.material || 'Not Specified',
          standard: data.standard || 'Not Specified',
          description: data.description || 'No description available',
          specifications: data.specifications || [],
          images: data.images || [],
          colors: data.colors || '',
          status: 'APPROVED',
          views: data.views || 0,
          favorites: data.favorites || 0,
          unit: data.unit || 'PCS'
        });
      });

      console.log(`Approved products loaded: ${productsList.length}`);
      console.log('Sample product with new fields:', productsList[0] ? {
        name: productsList[0].name,
        gst: productsList[0].gst,
        warranty: productsList[0].warranty,
        moq: productsList[0].moq
      } : 'No products');
      
      const sortedProducts = productsList.sort((a, b) => {
        return (b.createdAtTimestamp || 0) - (a.createdAtTimestamp || 0);
      });

      setAllProducts(sortedProducts);
      setFilteredProducts(sortedProducts);
      updateDisplayedProducts(sortedProducts, 1);
      
      // Show success message at bottom
      showBottomSuccessMessage(`${sortedProducts.length} approved products loaded successfully`);
      
    } catch (error) {
      console.error('Error fetching approved products:', error);
      
      if (error.message?.includes('index') || error.code === 'failed-precondition') {
        console.warn('Firestore index may need to be created. Falling back to client-side filtering...');
        showBottomSuccessMessage('Loading approved products...');
        
        await fetchAllAndFilterApproved();
      } else {
        toast.error(`Failed to load products: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAndFilterApproved = async () => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef, 
        orderBy('createdAtTimestamp', 'desc'),
        limit(1000)
      );

      const querySnapshot = await getDocs(q);
      const productsList = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        if (data.status !== 'APPROVED') {
          return;
        }
        
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
        const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date();
        const createdAtTimestamp = data.createdAtTimestamp || 
                                (data.createdAt?.toDate ? data.createdAt.toDate().getTime() : Date.now());
        
        productsList.push({
          id: doc.id,
          ...data,
          createdAt,
          updatedAt,
          createdAtTimestamp,
          name: data.name || 'Unnamed Product',
          category: data.category || 'Uncategorized',
          price: data.price || 0,
          gst: data.gst || '18%',
          warranty: data.warranty || 'No Warranty', // NEW: Fetch warranty field
          moq: data.moq || 1, // NEW: Fetch MOQ field
          rating: data.rating || 0,
          reviewCount: data.reviewCount || 0,
          stock: data.stock || 0,
          sellerName: data.sellerName || 'Unknown Seller',
          sellerVerified: data.sellerVerified || false,
          businessType: data.businessType || 'Manufacturer',
          city: data.city || 'Not Specified',
          material: data.material || 'Not Specified',
          standard: data.standard || 'Not Specified',
          description: data.description || 'No description available',
          specifications: data.specifications || [],
          images: data.images || [],
          colors: data.colors || '',
          status: 'APPROVED',
          views: data.views || 0,
          favorites: data.favorites || 0,
          unit: data.unit || 'PCS'
        });
      });

      console.log(`Approved products (client-filtered): ${productsList.length}`);
      console.log('Sample product with warranty:', productsList[0]?.warranty);
      console.log('Sample product with MOQ:', productsList[0]?.moq);
      
      setAllProducts(productsList);
      setFilteredProducts(productsList);
      updateDisplayedProducts(productsList, 1);
      
      showBottomSuccessMessage(`${productsList.length} approved products loaded`);
    } catch (error) {
      console.error('Error in fallback fetch:', error);
      toast.error('Failed to load approved products');
    }
  };

  // Format colors for display (truncate if too long)
  const formatColors = (colors) => {
    if (!colors || colors.trim() === '') return 'Not specified';
    
    // Split by common separators
    const colorList = colors.split(/[,+&|/]/).map(c => c.trim()).filter(c => c);
    
    if (colorList.length <= 2) {
      return colorList.join(', ');
    } else {
      return `${colorList[0]}, ${colorList[1]} +${colorList.length - 2} more`;
    }
  };

  // Get product image helper function
  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      // Handle different image formats
      if (typeof firstImage === 'string') return firstImage;
      if (firstImage?.data) return firstImage.data;
      if (firstImage?.url) return firstImage.url;
    }
    if (product.image) return product.image;
    if (product.productImage) return product.productImage;
    return '/placeholder-image.jpg';
  };

  // Sort products
  const sortProducts = (products, sortMethod) => {
    const sorted = [...products];
    
    switch (sortMethod) {
      case 'price-low':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-high':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'rating':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'newest':
        return sorted.sort((a, b) => {
          const dateA = a.createdAtTimestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          const dateB = b.createdAtTimestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          return dateB - dateA;
        });
      case 'popular':
      default:
        return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
    }
  };

  const updateDisplayedProducts = (products, page = 1) => {
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const pageProducts = products.slice(startIndex, endIndex);
    
    setDisplayedProducts(pageProducts);
    setCurrentPage(page);
    setTotalPages(Math.ceil(products.length / productsPerPage));
  };

  const applyFilters = () => {
    let filtered = [...allProducts];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => {
        return (
          product.name?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.sellerName?.toLowerCase().includes(query) ||
          product.material?.toLowerCase().includes(query) ||
          product.colors?.toLowerCase().includes(query) ||
          product.gst?.toLowerCase().includes(query) ||
          product.warranty?.toLowerCase().includes(query) || // NEW: Search in warranty
          (product.specifications && product.specifications.some(spec => 
            spec.toLowerCase().includes(query)
          ))
        );
      });
    }

    if (filters.inStockOnly) {
      filtered = filtered.filter(product => product.stock > 0);
    }

    const sortedFiltered = sortProducts(filtered, sortBy);
    setFilteredProducts(sortedFiltered);
    updateDisplayedProducts(sortedFiltered, 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateDisplayedProducts(filteredProducts, page);
  };

  const viewProductImages = (product) => {
    setSelectedProductForImages(product);
    setSelectedImageIndex(0);
    setShowImageModal(true);
  };

  const nextImage = () => {
    if (selectedProductForImages && selectedProductForImages.images) {
      const nextIndex = (selectedImageIndex + 1) % selectedProductForImages.images.length;
      setSelectedImageIndex(nextIndex);
    }
  };

  const prevImage = () => {
    if (selectedProductForImages && selectedProductForImages.images) {
      const prevIndex = (selectedImageIndex - 1 + selectedProductForImages.images.length) % selectedProductForImages.images.length;
      setSelectedImageIndex(prevIndex);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    }
    
    if (isRightSwipe) {
      prevImage();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleViewDetails = (product) => {
    navigate(`/buyer/products/${product.id}`, { state: { product } });
  };

  const handleInquiry = (product) => {
    console.log('Inquiry for:', product);
    toast.success(`Inquiry sent for ${product.name}`);
  };

  const formatINR = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest First' }
  ];

  useEffect(() => {
    if (allProducts.length > 0) {
      applyFilters();
    }
  }, [filters, sortBy, searchQuery]);

  useEffect(() => {
    fetchApprovedProducts();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Industrial Equipment & Components
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Source from verified suppliers worldwide with trade assurance. Showing only approved products.
              </p>
            </div>
            
            <div className="flex items-center justify-center lg:justify-end gap-4 sm:gap-6">
              <div className="text-center min-w-[70px] sm:min-w-[80px]">
                <div className="text-xl sm:text-2xl font-bold text-orange-600">
                  {allProducts.length.toLocaleString()}+
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Products</div>
              </div>
              <div className="text-center min-w-[70px] sm:min-w-[80px]">
                <div className="text-xl sm:text-2xl font-bold text-orange-600">5K+</div>
                <div className="text-xs sm:text-sm text-gray-600">Suppliers</div>
              </div>
              <div className="text-center min-w-[70px] sm:min-w-[80px]">
                <div className="text-xl sm:text-2xl font-bold text-orange-600">98%</div>
                <div className="text-xs sm:text-sm text-gray-600">Satisfaction</div>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Input
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-20 py-2.5 sm:py-3 text-sm sm:text-base border-orange-300 focus:border-orange-500 focus:ring-orange-500"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 px-3 sm:px-4 py-1.5 bg-orange-600 text-white font-medium rounded text-xs sm:text-sm hover:bg-orange-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Results Count and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <ShoppingBag className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-gray-700 text-sm sm:text-base">
                <span className="font-semibold text-gray-900">
                  {filteredProducts.length.toLocaleString()}
                </span> approved products found
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="lg:hidden relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer text-sm min-w-[140px]"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  title="Grid View"
                >
                  <Grid className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  title="List View"
                >
                  <List className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>

              <div className="hidden lg:block relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer text-sm min-w-[160px]"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="relative mb-4">
                <div className="h-16 w-16 border-4 border-gray-200 rounded-full"></div>
                <div className="absolute top-0 left-0 h-16 w-16 border-4 border-t-orange-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              </div>
              
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-gray-800">Loading Products</h3>
                <p className="text-sm text-gray-600 max-w-xs">
                  Fetching industrial equipment from verified suppliers...
                </p>
              </div>
            </div>
          ) : displayedProducts.length === 0 ? (
            <Card className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
              <p className="text-sm text-gray-600 mb-4">Try adjusting your search or filter criteria.</p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ inStockOnly: false });
                }} 
                className="bg-orange-600 hover:bg-orange-700 text-sm py-2"
              >
                Clear Search
              </Button>
            </Card>
          ) : viewMode === 'grid' ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {displayedProducts.map((product) => {
                  const warrantyInfo = formatWarranty(product.warranty);
                  const WarrantyIcon = warrantyInfo.icon;
                  
                  return (
                    <Card 
                      key={product.id} 
                      className="overflow-hidden hover:shadow-lg transition-shadow duration-200"
                    >
                      {/* Product Image */}
                      <div className="relative h-40 sm:h-48 overflow-hidden bg-gray-100 group">
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          className="w-full h-full object-contain cursor-pointer group-hover:scale-105 transition-transform duration-300"
                          onClick={() => viewProductImages(product)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                        
                        <button className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Heart className="w-4 h-4 text-gray-600" />
                        </button>

                        {/* Status Badge */}
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                            APPROVED
                          </span>
                        </div>
                      </div>

                      <div className="p-3 sm:p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-lg font-bold text-orange-600 flex items-center">
                              {formatINR(product.price)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              FOB Price / {product.unit || 'Piece'}
                            </div>
                          </div>
                          {product.rating > 0 && (
                            <div className="flex items-center bg-orange-50 px-2 py-0.5 rounded">
                              <Star className="w-3 h-3 text-orange-400 fill-current" />
                              <span className="text-xs font-semibold text-orange-700 ml-1">
                                {product.rating}
                              </span>
                            </div>
                          )}
                        </div>

                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 h-10">
                          {product.name}
                        </h3>

                        {/* Warranty Display - Professional Badge */}
                        {product.warranty && (
                          <div className={`flex items-center text-xs ${warrantyInfo.color} mb-2 ${warrantyInfo.bgColor} px-2 py-1 rounded-md border ${warrantyInfo.borderColor}`}>
                            <WarrantyIcon className="w-3 h-3 mr-1.5 flex-shrink-0" />
                            <span className="truncate font-medium" title={product.warranty}>
                              {warrantyInfo.text}
                            </span>
                          </div>
                        )}

                        {/* GST Display */}
                        {product.gst && (
                          <div className="flex items-center text-xs text-gray-600 mb-1">
                            <Percent className="w-3 h-3 mr-1.5 text-gray-400" />
                            <span>GST: {product.gst}</span>
                          </div>
                        )}

                        {/* Colors Display */}
                        {product.colors && product.colors.trim() !== '' && (
                          <div className="flex items-center text-xs text-gray-600 mb-1">
                            <Palette className="w-3 h-3 flex-shrink-0 mr-1.5 text-gray-400" />
                            <span className="truncate" title={product.colors}>
                              {formatColors(product.colors)}
                            </span>
                          </div>
                        )}

                        {/* MOQ Display - Professional with Gauge Icon */}
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <Gauge className="w-3 h-3 mr-1.5 text-gray-400" />
                          <span>MOQ: {formatMOQ(product.moq, product.unit)}</span>
                        </div>

                        {product.description && (
                          <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                            {product.description.substring(0, 80)}...
                          </p>
                        )}

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center">
                            <Building className="w-3 h-3 text-gray-400 mr-1.5" />
                            <span className="text-xs text-gray-600 truncate max-w-[100px]">
                              {product.sellerName}
                            </span>
                          </div>
                          {product.sellerVerified && (
                            <div className="flex items-center text-green-600 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Verified
                            </div>
                          )}
                        </div>

                        <div className="mt-4">
                          <button
                            onClick={() => handleViewDetails(product)}
                            className="w-full py-2 text-sm font-medium text-orange-600 border border-orange-300 rounded hover:bg-orange-50 transition-colors flex items-center justify-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Details
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              
              {filteredProducts.length > displayedProducts.length && (
                <div className="mt-6">
                  <div className="text-center text-sm text-gray-600 mb-4">
                    Showing {displayedProducts.length} of {filteredProducts.length} products
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center justify-center space-x-2 w-full sm:w-auto">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex-1 sm:flex-none px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </button>
                      
                      <div className="px-4 py-2.5 bg-gray-100 rounded-lg text-sm font-medium">
                        Page {currentPage} of {totalPages}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex-1 sm:flex-none px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                    
                    <div className="hidden lg:flex items-center space-x-2">
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          if (pageNum < 1 || pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                                currentPage === pageNum
                                  ? 'bg-orange-600 text-white'
                                  : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // List View
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {displayedProducts.map((product, index) => {
                const warrantyInfo = formatWarranty(product.warranty);
                const WarrantyIcon = warrantyInfo.icon;
                
                return (
                  <div 
                    key={product.id} 
                    className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${index === displayedProducts.length - 1 ? 'border-b-0' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row">
                      <div className="w-full sm:w-40 h-40 sm:h-auto flex-shrink-0">
                        <div className="relative w-full h-full">
                          <img
                            src={getProductImage(product)}
                            alt={product.name}
                            className="w-full h-full object-contain cursor-pointer"
                            onClick={() => handleViewDetails(product)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/placeholder-image.jpg';
                            }}
                          />
                          
                          <button className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow hover:bg-gray-100">
                            <Heart className="w-4 h-4 text-gray-600" />
                          </button>

                          {/* GST Badge for List View */}
                          {product.gst && (
                            <div className="absolute bottom-2 left-2">
                              <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full flex items-center">
                                <Percent className="w-3 h-3 mr-1" />
                                {product.gst}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 p-3 sm:p-4">
                        <div className="flex flex-col lg:flex-row lg:justify-between">
                          <div className="flex-1 mb-3 lg:mb-0 lg:pr-4">
                            <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
                              {product.name}
                            </h3>
                            
                            {/* Warranty Display in List View - Professional */}
                            {product.warranty && (
                              <div className={`inline-flex items-center text-xs ${warrantyInfo.color} ${warrantyInfo.bgColor} px-2 py-1 rounded-md border ${warrantyInfo.borderColor} mb-2`}>
                                <WarrantyIcon className="w-3 h-3 mr-1.5" />
                                <span className="font-medium">{warrantyInfo.text}</span>
                              </div>
                            )}
                            
                            {/* GST Display in List View */}
                            {product.gst && (
                              <div className="flex items-center text-sm text-gray-600 mb-1">
                                <Percent className="w-3 h-3 mr-1.5 text-gray-400" />
                                <span className="font-medium">GST:</span>
                                <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                  {product.gst}
                                </span>
                              </div>
                            )}
                            
                            {/* Colors Display in List View */}
                            {product.colors && product.colors.trim() !== '' && (
                              <div className="flex items-center text-sm text-gray-600 mb-2">
                                <Palette className="w-3 h-3 flex-shrink-0 mr-1.5 text-gray-400" />
                                <span className="truncate" title={product.colors}>
                                  <span className="font-medium">Colors:</span> {formatColors(product.colors)}
                                </span>
                              </div>
                            )}

                            {/* MOQ Display in List View - Professional */}
                            <div className="flex items-center text-sm text-gray-600 mb-2">
                              <Gauge className="w-3 h-3 mr-1.5 text-gray-400" />
                              <span className="font-medium">MOQ:</span>
                              <span className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">
                                {formatMOQ(product.moq, product.unit)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between mb-2">
                              <div className="text-lg font-bold text-orange-600 flex items-center">
                                {formatINR(product.price)}
                              </div>
                              {product.rating > 0 && (
                                <div className="flex items-center bg-orange-50 px-2 py-0.5 rounded">
                                  <Star className="w-3 h-3 text-orange-400 fill-current" />
                                  <span className="text-xs font-semibold text-orange-700 ml-1">
                                    {product.rating}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1 text-sm text-gray-600 mb-3">
                              <div className="flex items-center">
                                <Building className="w-3 h-3 text-gray-400 mr-1.5" />
                                <span className="truncate">{product.sellerName || 'Unknown Seller'}</span>
                                {product.sellerVerified && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                    <Check className="w-2 h-2 inline mr-1" />
                                    Verified
                                  </span>
                                )}
                              </div>
                            </div>

                            {product.description && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                {product.description}
                              </p>
                            )}
                          </div>

                          <div className="w-full lg:w-40 flex-shrink-0">
                            <div className="space-y-2">
                              <button
                                onClick={() => handleViewDetails(product)}
                                className="w-full py-2 text-sm font-medium text-orange-600 border border-orange-300 rounded hover:bg-orange-50 transition-colors flex items-center justify-center"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Success Message */}
      {showSuccessMessage && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-slideUp">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Mobile Image Viewer Modal */}
      {showImageModal && selectedProductForImages && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center">
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 z-10 p-2 text-white hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={selectedProductForImages.images?.[selectedImageIndex]?.data || getProductImage(selectedProductForImages)}
              alt={`${selectedProductForImages.name} - Image ${selectedImageIndex + 1}`}
              className="max-h-full max-w-full object-contain"
            />
            
            {selectedProductForImages.images && selectedProductForImages.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronLeft className="h-6 w-6 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronRight className="h-6 w-6 text-white" />
                </button>
              </>
            )}

            {selectedProductForImages.images && selectedProductForImages.images.length > 1 && (
              <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2">
                {selectedProductForImages.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`w-2 h-2 rounded-full ${idx === selectedImageIndex ? 'bg-orange-500' : 'bg-white/50'}`}
                  />
                ))}
              </div>
            )}

            {selectedProductForImages.images && selectedProductForImages.images.length > 1 && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-sm opacity-75">
                Swipe to navigate images
              </div>
            )}
          </div>

          {/* Product Info at Bottom - Updated with Warranty and MOQ */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <h3 className="text-white font-semibold mb-1">{selectedProductForImages.name}</h3>
            <div className="text-white/90 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="font-medium">Price:</span>
                  <span className="ml-2 text-orange-300 font-bold">
                    {formatINR(selectedProductForImages.price)}
                  </span>
                </div>
                <div className="text-xs px-2 py-1 bg-green-900/50 text-green-300 rounded">
                  APPROVED
                </div>
              </div>
              
              {/* Display Warranty in modal */}
              {selectedProductForImages.warranty && (
                <div className="flex items-center mt-1">
                  <Shield className="w-3 h-3 text-gray-300 mr-1.5" />
                  <span className="text-white/80">Warranty: {selectedProductForImages.warranty}</span>
                </div>
              )}
              
              {/* Display GST in modal */}
              {selectedProductForImages.gst && (
                <div className="flex items-center mt-1">
                  <Percent className="w-3 h-3 text-gray-300 mr-1.5" />
                  <span className="text-white/80">GST: {selectedProductForImages.gst}</span>
                </div>
              )}
              
              {/* Display MOQ in modal */}
              {selectedProductForImages.moq && (
                <div className="flex items-center mt-1">
                  <Gauge className="w-3 h-3 text-gray-300 mr-1.5" />
                  <span className="text-white/80">MOQ: {formatMOQ(selectedProductForImages.moq, selectedProductForImages.unit)}</span>
                </div>
              )}
              
              {/* Display colors in modal */}
              {selectedProductForImages.colors && selectedProductForImages.colors.trim() !== '' && (
                <div className="flex items-center mt-1">
                  <Palette className="w-3 h-3 text-gray-300 mr-1.5" />
                  <span className="text-white/80 truncate" title={selectedProductForImages.colors}>
                    Colors: {formatColors(selectedProductForImages.colors)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        @media (min-width: 475px) {
          .xs\\:inline-block {
            display: inline-block;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsPage;